import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ListHelperService, ProfileService, StoreService } from 'core/services';
import { TableDatasource, DataTypes, TableColumn } from '@gamelearn/skeletor/models';
import { TranslateService } from '@ngx-translate/core';
import { KeplerService } from '../../services/kepler.service';
import { Router } from '@angular/router';
import { DEFAULT_RESPONSE_TABLE } from '../../assets/constants';
import { KeplerMatchList } from '../../models/KeplerMatch';

@Component({
  selector: 'lms-kepler-match-list',
  templateUrl: './kepler-match-list.component.html',
  styleUrls: ['./kepler-match-list.component.scss']
})
export class KeplerMatchListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public listId: string;
  @Input() public clientId: string;
  @Input() public matchList: KeplerMatchList;
  private keplerMatchSubscription: Subscription = new Subscription();
  public dataSource: any;
  private userRole: any;

  constructor(
    private profileService: ProfileService,
    private translateService: TranslateService,
    private listHelperService: ListHelperService,
    private keplerService: KeplerService,
    private router: Router
  ) {}

  public ngOnInit(): void {
    this.keplerMatchSubscription = this.getLoggedUserRole$().subscribe(() => {
      this.dataSource = this.getDataSource(this.listId, this.userRole, this.matchList);
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.dataSource = this.getDataSource(this.listId, this.userRole, changes.matchList.currentValue);
  }

  public ngOnDestroy(): void {
    this.keplerMatchSubscription.unsubscribe();
  }

  /**
   * Get the role of the logged user
   * @private
   */
  private getLoggedUserRole$(): Observable<any> {
    return this.profileService.getLoggedUserRole$().pipe(
      tap((userRole) => {
        this.userRole = userRole;
      })
    );
  }

  /**
   * Fill the data with the table
   * @param listId
   * @param userRole
   * @param keplerMatchDatasource
   */
  public getDataSource(listId: string, userRole: any, keplerMatchDatasource: any): TableDatasource {
    let dataSource = new TableDatasource({});
    if (keplerMatchDatasource) {
      dataSource = new TableDatasource({
        docs: keplerMatchDatasource.docs,
        columns: this.getColumns(),
        limit: keplerMatchDatasource.limit,
        page: keplerMatchDatasource.page,
        pages: keplerMatchDatasource.pages,
        total: keplerMatchDatasource.total,
        sort: `-startDate`,
        load: (props) =>
          this.loadTableDataSource(this.clientId, props, dataSource).subscribe(() => dataSource.refresh()),
        storeState: {
          enabled: true,
          key: `${listId}_${userRole.name}`
        }
      });
      StoreService.create(listId, dataSource);
    }
    return dataSource;
  }

  /**
   * Set the data of the table
   * @param dataSource
   */
  public setDatasourceUpdate(dataSource: any): void {
    this.dataSource = dataSource;
    StoreService.update(this.listId, this.dataSource);
  }

  onClickRow(match) {
    this.router.navigateByUrl(`/clients/${this.clientId}/kepler/${match.id}`);
  }
  /**
   * Get the columns of the table
   * @private
   */
  private getColumns(): TableColumn[] {
    return [
      {
        source: 'name',
        label: this.translateService.instant('kepler.name'),
        dataType: DataTypes.string,
        sortable: true,
        style: { width: '160px' },
        cellRenderer: (renderer, value, selectedMatch) =>
          this.listHelperService.getRouterLinkRenderer(
            renderer,
            `${this.clientId ? `/clients/${this.clientId}` : 'kepler'}/kepler/${selectedMatch.id}`,
            value
          )
      },
      {
        source: 'playerCount',
        label: this.translateService.instant('kepler.students'),
        dataType: DataTypes.number,
        allowFiltering: false,
        sortable: true,
        style: { width: '100px' }
      },
      {
        source: 'startDate',
        label: this.translateService.instant('nouns.startDate'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '110px' },
        valueParser: (value, group) => this.listHelperService.parseDate(value, group.timezone)
      },
      {
        source: 'endDate',
        label: this.translateService.instant('nouns.endDate'),
        dataType: DataTypes.datetime,
        sortable: true,
        style: { width: '110px' },
        valueParser: (value, group) => this.listHelperService.parseDate(value, group.timezone)
      },
      {
        source: 'status',
        label: this.translateService.instant('nouns.status'),
        style: { width: '130px' },
        valueParser: (value, matchData) => this.parseStateValue(value, matchData),
        searchRenderer: (renderer, value, callback) => {
          const options = [
            { key: 'any', value: this.translateService.instant('properties.any') },
            { key: 'active', value: this.translateService.instant('properties.active') },
            { key: 'ended', value: this.translateService.instant('properties.finished') },
            { key: 'pending', value: this.translateService.instant('properties.future') }
          ];
          return renderer`<gl-select small .options="${options}" .value="${value || 'any'}" @onChange="${(e) =>
            callback(e.detail.value !== 'any' ? e.detail.value : '')}" ></gl-select>`;
        }
      }
    ];
  }

  /**
   * Load the table
   * @private
   */
  private loadTableDataSource(clientId: string, props: any, dataSource: any): Observable<KeplerMatchList> {
    return this.keplerService.fetch$(clientId, props, dataSource).pipe(
      tap((response) => {
        dataSource.docs = response?.docs || DEFAULT_RESPONSE_TABLE.docs;
        dataSource.total = response?.total || DEFAULT_RESPONSE_TABLE.total;
        dataSource.limit = response?.limit || DEFAULT_RESPONSE_TABLE.limit;
        dataSource.page = response?.page || DEFAULT_RESPONSE_TABLE.page;
        dataSource.pages = response?.pages || DEFAULT_RESPONSE_TABLE.pages;
      })
    );
  }

  /**
   * Parse the datetime of the matches
   * @param data
   * @param matchData
   * @private
   */
  private parseStateValue(data, matchData: any): string {
    const startDiffDates = this.listHelperService.compareDateTimeNow(matchData.startDate, matchData.timezone);
    const endDiffDates = this.listHelperService.compareDateTimeNow(matchData.endDate, matchData.timezone);

    if (endDiffDates < 0) {
      return this.translateService.instant('properties.finished');
    } else if (startDiffDates < 0 && endDiffDates > 0) {
      return this.translateService.instant('properties.active');
    } else if (endDiffDates > 0) {
      return this.translateService.instant('properties.future');
    }
  }
}
